import { Box } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { CategoryLinkList } from '@portals/core/src/components/CategoryLinkList/CategoryLinkList';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import {
  SparkasseAssignment,
  SparkasseAssignmentFormState,
} from '@portals/forms/src/containers/SparkasseAssignment/SparkasseAssignment';
import { useSPKCookie } from '@portals/forms/src/hooks/useSPKCookie/useSPKCookie';
import { LinkTracking, TrackingContext } from '@portals/tracking/src/context/tracking';
import { trackSparkasseAssignmentForm } from '@portals/tracking/src/forms/trackSparkasseAssignmentForm';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FormsSparkasseAssignmentForm as SparkasseAssignmentFormModel,
  isLinkThematischerLink,
  LinkThematischerLink,
} from '../../../models';
import { InternCategoryLink } from '../../components/InternCategoryLink/InternCategoryLink';
import { useSparkassenAssignmentForm } from '../../contexts/sparkasse-assignment-form';
import { useLinkProps } from '../../hooks/useLinkProps';
import { useThematicLink } from '../../hooks/useThematicLink';
import { ArrowRight } from '../../utils/loadIcon';
import { getRedirectTimeout } from '../../utils/thematischerLink';

interface Props {
  node: SparkasseAssignmentFormModel;
}

const LinkButton = ({ node, sparkasseKnown }) => {
  const link = node?.initalButton?.link?.at(0);
  const { singleProps } = useLinkProps(link);
  const [href, setHref] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isLinkThematischerLink(link)) {
      setHref(sparkasseKnown ? singleProps.href : undefined);
    } else {
      setHref(singleProps.href);
    }
  }, [sparkasseKnown, singleProps.href, link]);

  return (
    !!link && (
      <Button variant="standard" {...singleProps} href={href} fullWidth endIcon={<ArrowRight />}>
        {link.text}
      </Button>
    )
  );
};

const ScrollToForm = ({ link, formRef, sparkasseKnown, setShowForm, setShowRedirect }) => {
  const { identifier, findNearestForm } = useSparkassenAssignmentForm();

  useThematicLink((key, description) => {
    if (identifier(link?.key, link?.description) !== identifier(key, description)) {
      return;
    }
    const form = findNearestForm(key, description);
    if (formRef.current !== form) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          if (entry.isIntersecting) {
            setShowForm(true);
            if (sparkasseKnown) {
              setShowRedirect(true);
            }
            observer.disconnect();
          }
        });
      },
      {
        threshold: 1,
      }
    );
    observer.observe(form);
  });

  return null;
};

export const SparkasseAssignmentForm: React.FunctionComponent<Props> = ({ node }) => {
  const { bankCode } = useSPKCookie();

  const link = node?.initalButton?.link.at(0) as LinkThematischerLink;
  const { useFormRef, resolveLink } = useSparkassenAssignmentForm();
  const formRef = useFormRef(link?.key, link?.description);
  const timeout = getRedirectTimeout(link, bankCode);

  const currentFormRef = useRef(null);
  const currentFormRefCallback = useCallback((node) => {
    currentFormRef.current = node;
    formRef(node);
  }, []);

  const handleRedirect = useCallback(
    () => resolveLink(link?.key, link?.description, bankCode),
    [link?.key, link?.description, bankCode, resolveLink]
  );

  const handleTracking = useCallback(
    (payload: LinkTracking) => {
      trackSparkasseAssignmentForm({
        id: `${node.id}`,
        name: node.assignmentHeadline,
        spk: bankCode,
        key: link?.key,
        description: link?.description,
        ...payload,
      });
    },
    [bankCode, node.assignmentHeadline, node.id, link?.key, link?.description]
  );

  return (
    <TrackingContext.Provider value={{ trackLink: handleTracking }}>
      <SparkasseAssignment
        headline={node.initialHeadline}
        subline={node.initialSubline}
        ref={currentFormRefCallback}
        RedirectBoxProps={{
          redirect: handleRedirect,
          timeout,
        }}
        AssignmentFormProps={{
          headline: node.assignmentHeadline,
          subline: node.assignmentSubline,
        }}
        Button={<LinkButton node={node} sparkasseKnown={!!bankCode} />}
      >
        {(props: SparkasseAssignmentFormState) => (
          <>
            {node.initialLinks && node.initialLinks.length > 0 && (
              <Box>
                <CategoryLinkList>
                  <Typography variant="h6" innerPadding="both">
                    Weitere Optionen
                  </Typography>
                  {node.initialLinks.map((entry) => (
                    <InternCategoryLink key={entry.id} node={entry} />
                  ))}
                </CategoryLinkList>
              </Box>
            )}
            <ScrollToForm link={link} formRef={currentFormRef} sparkasseKnown={!!bankCode} {...props} />
          </>
        )}
      </SparkasseAssignment>
    </TrackingContext.Provider>
  );
};
